import { z } from 'zod';

const CCY = z.string().length(3).regex(/^[A-Z]{3}$/);
const DECIMAL = z.string().regex(/^\d+(\.\d{1,4})?$/);

// ---- Expense categories ----
export const ExpenseCategorySchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string().min(1).max(150),
  /** Optional default expense GL account this category posts to. */
  defaultAccountId: z.string().uuid().nullable(),
  isActive: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type ExpenseCategory = z.infer<typeof ExpenseCategorySchema>;

export const ExpenseCategoryCreateSchema = z.object({
  name: z.string().min(1).max(150),
  defaultAccountId: z.string().uuid().nullable().optional(),
});
export type ExpenseCategoryCreate = z.infer<typeof ExpenseCategoryCreateSchema>;

export const ExpenseCategoryUpdateSchema = z.object({
  name: z.string().min(1).max(150).optional(),
  defaultAccountId: z.string().uuid().nullable().optional(),
  isActive: z.boolean().optional(),
});
export type ExpenseCategoryUpdate = z.infer<typeof ExpenseCategoryUpdateSchema>;

// ---- Expenses ----
export const ExpenseStatusSchema = z.enum(['posted', 'void']);
export type ExpenseStatus = z.infer<typeof ExpenseStatusSchema>;

export const ExpenseSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  expenseNumber: z.string(),
  expenseDate: z.string(),
  categoryId: z.string().uuid().nullable(),
  categoryName: z.string().nullable(),
  /** The GL account debited (the cost). */
  expenseAccountId: z.string().uuid(),
  expenseAccountName: z.string().nullable(),
  /** The cash/bank account credited (paid from). */
  paidFromAccountId: z.string().uuid(),
  paidFromAccountName: z.string().nullable(),
  payee: z.string().nullable(),
  amount: z.string(),
  currencyCode: z.string(),
  description: z.string().nullable(),
  journalEntryId: z.string().uuid().nullable(),
  /** The branch (warehouse) that spent it; null = unbranched. */
  warehouseId: z.string().uuid().nullable().optional(),
  warehouseName: z.string().nullable().optional(),
  status: ExpenseStatusSchema,
  createdByUserId: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Expense = z.infer<typeof ExpenseSchema>;

// ---- Report (aggregations; posted only, grouped by currency) ----
export const ExpenseReportTotalSchema = z.object({
  currencyCode: z.string(),
  total: z.string(),
  count: z.number().int().nonnegative(),
});
export const ExpenseReportCategorySchema = z.object({
  categoryId: z.string().uuid().nullable(),
  categoryName: z.string().nullable(),
  currencyCode: z.string(),
  total: z.string(),
  count: z.number().int().nonnegative(),
});
export const ExpenseReportMonthSchema = z.object({
  /** "YYYY-MM" */
  month: z.string(),
  currencyCode: z.string(),
  total: z.string(),
  count: z.number().int().nonnegative(),
});
export const ExpenseReportSchema = z.object({
  totals: z.array(ExpenseReportTotalSchema),
  byCategory: z.array(ExpenseReportCategorySchema),
  byMonth: z.array(ExpenseReportMonthSchema),
});
export type ExpenseReport = z.infer<typeof ExpenseReportSchema>;
export type ExpenseReportCategory = z.infer<typeof ExpenseReportCategorySchema>;
export type ExpenseReportMonth = z.infer<typeof ExpenseReportMonthSchema>;

export const ExpenseCreateSchema = z.object({
  expenseDate: z.string(),
  categoryId: z.string().uuid().nullable().optional(),
  expenseAccountId: z.string().uuid(),
  paidFromAccountId: z.string().uuid(),
  payee: z.string().max(200).nullable().optional(),
  amount: DECIMAL,
  currencyCode: CCY,
  description: z.string().max(1000).nullable().optional(),
  /** The branch (warehouse) that spent it. Null = unbranched / head office. */
  warehouseId: z.string().uuid().nullable().optional(),
});
export type ExpenseCreate = z.infer<typeof ExpenseCreateSchema>;

/** Partial edit of a posted expense. Money fields trigger a GL reverse + repost. */
export const ExpenseUpdateSchema = z.object({
  expenseDate: z.string().optional(),
  categoryId: z.string().uuid().nullable().optional(),
  expenseAccountId: z.string().uuid().optional(),
  paidFromAccountId: z.string().uuid().optional(),
  payee: z.string().max(200).nullable().optional(),
  amount: DECIMAL.optional(),
  currencyCode: CCY.optional(),
  description: z.string().max(1000).nullable().optional(),
  warehouseId: z.string().uuid().nullable().optional(),
});
export type ExpenseUpdate = z.infer<typeof ExpenseUpdateSchema>;
